/**
 * Components - Main component library (Infrastructure & Design System)
 *
 * This is the unified component system containing only infrastructure and shared utilities.
 * All business features have been moved to @/features/{feature} and page components to @/views/{feature}.
 *
 * Structure:
 * - ui: Basic UI primitives (Button, Card, Text, Tabs, Modal, Drawer, etc.) - Design System
 *   (Class-specific components moved to @/views/classes/components, WaiversList to @/views/terms/components)
 * - forms: Form components with RHF integration - Shared Library
 * - feedback: LoadingState, EmptyState, Alert, Toast - Shared Utilities
 *   (ClassesEmptyState moved to @/views/classes/components)
 * - layout: Layout components (PublicLayout, DashboardLayout, etc.) - Infrastructure
 * - theme: Theme token helpers and hooks - Infrastructure
 * - dashboard: Dashboard theme provider and theme utilities - Infrastructure
 *   (Dashboard variants moved to @/features/dashboard/shared/variants)
 * - shared: Shared utilities and form elements - Shared Library
 * - icons: Icon components - Shared Utilities
 * - scrollbar, scrollToTop: Utility components - Shared Utilities
 *
 * Business Features:
 * - All business features moved to @/features/{feature}/{admin|member|shared}
 * - Page-level components moved to @/views/{feature}
 */

// Core component categories
export * from "./feedback";
export * from "./forms";
// Page components moved to @/views or @/features/dashboard
// Theme exports - re-export from lib/theme for backward compatibility
export * from "@/lib/theme/tokens";
export type { DashboardMode } from "@/components/dashboard/DashboardThemeProvider";
export * from "./ui";
export * from "./layout";

// Domain-specific components
// Appointments feature moved to @/features/appointments
// Import from @/features/appointments/admin, @/features/appointments/member, or @/features/appointments/shared
// Classes feature moved to @/features/classes
// Import from @/features/classes/admin, @/features/classes/member, or @/features/classes/shared
// Class UI components (ClassCard, ClassesGrid, ClassesList, ClassesTable, ClassesEmptyState) moved to @/views/classes/components
export * from "./icons/Icons";
// Parents feature moved to @/features/parents
// Import from @/features/parents/admin or @/features/parents/shared
// Dashboard feature moved to @/features/dashboard
// Import from @/features/dashboard/admin, @/features/dashboard/member, or @/features/dashboard/shared
// Dashboard variants moved to @/features/dashboard/shared/variants
// Profile-settings feature moved to @/features/profile-settings
// Import from @/features/profile-settings/member or @/features/profile-settings/shared
export * from "./scrollbar/Scrollbar";
// Shared exports - exclude SidebarWrapper which uses createContext
export * from "./shared/formElements/DeleteModal";
export * from "./shared/formElements/FormModal";
export * from "./shared/formElements/LabeledInput";
export * from "./shared/formElements/LabeledTextarea";
export * from "./shared/formElements/TimeInput";
export * from "./shared/NoDataFallback";
export * from "./shared/NotFoundClient";
export * from "./shared/formElements/ConfirmationModal";
export * from "./shared/formElements/InfoModal";
export * from "./shared/ModalDetailSection";
export * from "./shared/SelectionActions/SelectionActions";
export * from "./shared/sidebar/linkItems";
export * from "./shared/sidebar/SidebarContent";
export * from "./shared/sidebar/SidebarLinks";
export * from "./shared/TenantLogo";
// SidebarWrapper and contexts should be imported directly from @/components/shared/sidebar/SidebarWrapper
// to avoid SSR issues with createContext
// Students feature moved to @/features/students
// Import from @/features/students/admin, @/features/students/member, or @/features/students/shared
// Page components moved to @/views/students
// Teacher forms moved to @/features/employees/admin/teacher
// Import from @/features/employees/admin/teacher
// Employees feature moved to @/features/employees
// Import from @/features/employees/admin or @/features/employees/shared
